<script setup>
/**
 * The workroom — the one screen a ticket is actually worked on.
 *
 * It is the only place in this surface that fires a mutation: the top bar and
 * the three panes all emit intents, and every one of them lands on a store
 * action that maps to exactly one server-side command. That is why stage, owner
 * and archival are separate handlers rather than one save — each is its own
 * command carrying its own event, and the activity trail is refreshed after a
 * write so the history the screen shows is the server's, not a guess.
 *
 * A ticket that does not exist, or that this agent cannot see, is the same
 * screen: the API answers 404 for both on purpose (a 403 would confirm the
 * number is real), and so does this.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';

import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import CaseChatPane from 'dashboard/components-next/SupportCases/CaseChatPane.vue';
import CaseContentPane from 'dashboard/components-next/SupportCases/CaseContentPane.vue';
import CasePropertyRail from 'dashboard/components-next/SupportCases/CasePropertyRail.vue';
import CaseSidepanel from 'dashboard/components-next/SupportCases/CaseSidepanel.vue';
import CaseTopBar from 'dashboard/components-next/SupportCases/CaseTopBar.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useSupportCasesStore } from 'dashboard/stores/supportCases';

const props = defineProps({
  // The number agents quote, from the route path.
  displayId: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { isAdmin } = useAdmin();

const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');

const casesStore = useSupportCasesStore();
const { uiFlags } = storeToRefs(casesStore);

const hasError = ref(false);
const isMissing = ref(false);
const isFetchingActivities = ref(false);

const supportCase = computed(() => casesStore.caseByDisplayId(props.displayId));

const comments = computed(() =>
  supportCase.value ? casesStore.commentsFor(supportCase.value.id) : []
);

const tasks = computed(() =>
  supportCase.value ? casesStore.tasksFor(supportCase.value.id) : []
);

const activities = computed(() =>
  supportCase.value ? casesStore.activitiesFor(supportCase.value.id) : []
);

const isBusy = computed(() => uiFlags.value.isUpdatingItem);

// An archived ticket is the record of a finished one. The server refuses every
// mutation on it with a 422 (`SupportCases::Commands::Base`) except the archival
// pair, so the screen offers exactly what the server will accept: everything is
// read-only, and the one control left is the one that brings the ticket back.
const isArchived = computed(() =>
  Boolean(supportCase.value?.archivedAt || supportCase.value?.archived)
);

const isReadOnly = computed(() => isBusy.value || isArchived.value);

// The one link out to the customer. There is no case→customer message API, so
// the chat pane routes to the conversation instead of offering a send that
// would be refused; a conversation this agent cannot open is not offered at all.
const primaryConversationLink = computed(
  () =>
    (supportCase.value?.conversationLinks || []).find(
      link => link.role === 'primary_customer' && !link.conversation?.restricted
    ) || null
);

// The first load has nothing to show yet; a refetch of a case already on screen
// keeps the screen and lets the panels update under it.
const isLoading = computed(
  () => uiFlags.value.isFetchingItem && !supportCase.value
);

const loadActivities = async () => {
  isFetchingActivities.value = true;
  try {
    await casesStore.fetchActivities(props.displayId);
  } catch (error) {
    // The trail is context, not the ticket: failing to load it must not take
    // the workroom down with it.
  } finally {
    isFetchingActivities.value = false;
  }
};

const load = async () => {
  hasError.value = false;
  isMissing.value = false;
  try {
    await casesStore.fetchCase(props.displayId);
  } catch (error) {
    if (error?.response?.status === 404) isMissing.value = true;
    else hasError.value = true;
    return;
  }
  await loadActivities();
};

// The trail is written by an asynchronous listener that runs after the
// mutation's transaction commits, so a refetch fired the moment the write
// answers races its own entry. One retry, a second later, is the whole fix: if
// the trail grew, the entry landed; if it did not, either the command was an
// idempotent no-op (which emits nothing at all) or the listener is slower than
// any wait worth freezing a screen for, and the next load will show it.
const ACTIVITY_SETTLE_DELAY = 1000;

let settleTimer = null;

const refetchActivities = async () => {
  const before = activities.value.length;
  await loadActivities();
  if (activities.value.length !== before) return;

  // Not awaited: the mutation is finished either way, and the screen must not
  // sit disabled for a second waiting on a trail entry.
  clearTimeout(settleTimer);
  settleTimer = setTimeout(loadActivities, ACTIVITY_SETTLE_DELAY);
};

// Every write answers with the record, so the trail is refetched rather than
// appended to by hand: the listener writes it, and this screen reads it.
const runMutation = async mutation => {
  try {
    await mutation();
    await refetchActivities();
  } catch (error) {
    // One stated sentence rather than the server's: a 422 from a command reads
    // as an attribute error, which is not a sentence to hand an agent.
    useAlert(t('SUPPORT_CASES.DETAIL.MUTATION_ERROR'));
  }
};

const onUpdateTitle = title =>
  runMutation(() => casesStore.updateCase(props.displayId, { title }));

const onUpdateDescription = description =>
  runMutation(() => casesStore.updateCase(props.displayId, { description }));

// `priority` is an existing nullable column the update command already writes;
// clearing it back to null is as much an edit as setting one.
const onUpdatePriority = priority =>
  runMutation(() => casesStore.updateCase(props.displayId, { priority }));

const onChangeStage = stage =>
  runMutation(() => casesStore.changeStage(props.displayId, stage.id));

const onAssign = owner =>
  runMutation(() => casesStore.assignCase(props.displayId, owner));

const onArchive = () =>
  runMutation(() => casesStore.archiveCase(props.displayId));

const onUnarchive = () =>
  runMutation(() => casesStore.unarchiveCase(props.displayId));

// Custom fields are a merge, one field at a time: `null` clears a value rather
// than omitting it.
const onFieldUpdate = ({ key, value }) =>
  runMutation(() =>
    casesStore.updateCase(props.displayId, { customFields: { [key]: value } })
  );

const onAddTask = title =>
  runMutation(() => casesStore.addTask(props.displayId, { title }));

const onToggleTask = ({ task, completed }) =>
  runMutation(() => casesStore.toggleTask(props.displayId, task.id, completed));

const onArchiveTask = task =>
  runMutation(() => casesStore.archiveTask(props.displayId, task.id));

const onUnlink = link =>
  runMutation(() => casesStore.unlinkConversation(props.displayId, link.id));

const onPostComment = payload =>
  runMutation(() => casesStore.addComment(props.displayId, payload));

const onEditComment = ({ comment, content }) =>
  runMutation(() =>
    casesStore.editComment(props.displayId, comment.id, content)
  );

const onArchiveComment = comment =>
  runMutation(() => casesStore.archiveComment(props.displayId, comment.id));

// The conversation is addressed by its display id, which is what its own route
// takes; the link's `conversationId` is the internal id the writes take.
const onOpenConversation = link =>
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: String(link.conversation.displayId) },
  });

const onBack = () => router.push({ name: 'support_cases_index' });

// The number as the product spells it, because that is what an agent quotes in
// a handover — not the URL of the screen they are already on.
const onCopyId = async () => {
  await copyTextToClipboard(
    t('SUPPORT_CASES.LIST.DISPLAY_ID', { id: props.displayId })
  );
  useAlert(t('SUPPORT_CASES.DETAIL.COPY_ID_DONE'));
};

onMounted(() => {
  // The assign menu offers people and teams; both are account-wide lists the
  // rest of the dashboard already keeps.
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  load();
});

watch(() => props.displayId, load);

// A pending retry outlives nothing: leaving the workroom cancels it rather than
// firing a request into a screen that is gone.
onBeforeUnmount(() => clearTimeout(settleTimer));
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 gap-3">
    <CaseTopBar
      :display-id="displayId"
      :is-archived="isArchived"
      :is-busy="isBusy"
      @back="onBack"
      @copy-id="onCopyId"
      @unarchive="onUnarchive"
    />

    <div
      v-if="isLoading"
      role="status"
      aria-busy="true"
      :aria-label="t('SUPPORT_CASES.DETAIL.LOADING')"
      class="flex flex-col gap-4"
      data-testid="case-detail-skeleton"
    >
      <Skeleton variant="text" :announce="false" class="w-40" />
      <Skeleton variant="text" :announce="false" class="w-2/3 h-6" />
      <Skeleton variant="text" :announce="false" />
      <Skeleton variant="text" :announce="false" />
    </div>

    <EmptyStateLayout
      v-else-if="isMissing"
      compact
      icon="i-lucide-file-question"
      :title="t('SUPPORT_CASES.DETAIL.MISSING.TITLE')"
      :subtitle="t('SUPPORT_CASES.DETAIL.MISSING.SUBTITLE')"
      data-testid="case-detail-missing"
    >
      <template #actions>
        <Button
          size="sm"
          variant="outline"
          color="slate"
          :label="t('SUPPORT_CASES.DETAIL.MISSING.ACTION')"
          @click="onBack"
        />
      </template>
    </EmptyStateLayout>

    <EmptyStateLayout
      v-else-if="hasError || !supportCase"
      compact
      icon="i-lucide-triangle-alert"
      :title="t('SUPPORT_CASES.DETAIL.ERROR.TITLE')"
      :subtitle="t('SUPPORT_CASES.DETAIL.ERROR.SUBTITLE')"
      data-testid="case-detail-error"
    >
      <template #actions>
        <Button
          size="sm"
          :label="t('SUPPORT_CASES.DETAIL.ERROR.ACTION')"
          @click="load"
        />
      </template>
    </EmptyStateLayout>

    <!-- The ticket itself, and one sidepanel beside it holding the properties
         and the internal thread as tabs — the same switch the inbox makes
         between Details and Tola AI, so a stacked pair of regions is no longer
         splitting the column's height between them. -->
    <div
      v-else
      class="flex flex-col gap-4 overflow-y-auto min-h-0 grow xl:flex-row xl:gap-6 xl:overflow-hidden"
    >
      <CaseContentPane
        :support-case="supportCase"
        :tasks="tasks"
        :disabled="isReadOnly"
        class="xl:flex-1 xl:min-h-0 xl:overflow-y-auto"
        @update-title="onUpdateTitle"
        @update-description="onUpdateDescription"
        @add-task="onAddTask"
        @toggle-task="onToggleTask"
        @archive-task="onArchiveTask"
        @open-conversation="onOpenConversation"
        @unlink="onUnlink"
      />

      <CaseSidepanel class="xl:w-[22rem] xl:shrink-0 xl:min-h-0">
        <template #details>
          <CasePropertyRail
            :support-case="supportCase"
            :workflow-stages="supportCase.workflowStages || []"
            :assignable-users="agents"
            :assignable-teams="teams"
            :is-updating="isBusy"
            :is-locked="isArchived"
            :can-manage-types="isAdmin"
            @change-stage="onChangeStage"
            @assign="onAssign"
            @update-priority="onUpdatePriority"
            @update-field="onFieldUpdate"
            @archive="onArchive"
            @unarchive="onUnarchive"
          />
        </template>

        <template #thread>
          <CaseChatPane
            :case-id="supportCase.id"
            :comments="comments"
            :activities="activities"
            :current-user-id="currentUser?.id"
            :is-admin="isAdmin"
            :is-loading-thread="uiFlags.isFetchingThread"
            :is-loading-activities="isFetchingActivities"
            :known-users="agents"
            :known-teams="teams"
            :disabled="isReadOnly"
            :primary-conversation-link="primaryConversationLink"
            @post="onPostComment"
            @edit="onEditComment"
            @archive="onArchiveComment"
            @open-conversation="onOpenConversation"
          />
        </template>
      </CaseSidepanel>
    </div>
  </div>
</template>
